import { useCallback, useEffect, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from './client';
import { EconomyHealth } from './types';

const HOUR_MS = 60 * 60 * 1000;

/** All time, or an inclusive `from`/`to` in calendar days. */
export type EconomyWindow = { days: 'all' } | { from?: string; to?: string };

/**
 * Economy-health dashboard data (SSA-825). Fetches all 12 aggregates in one
 * payload, honors the window, auto-refreshes hourly, and exposes a manual
 * refresh. `lastUpdated` drives the "as of" label.
 */
export function useEconomyHealth(env: Env, win: EconomyWindow) {
  const { request } = useAdminApi();
  const [data, setData] = useState<EconomyHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [tick, setTick] = useState(0);
  const windowKey = JSON.stringify(win);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const payload = await request<EconomyHealth>({
          env,
          path: '/admin/gamification/economy-health',
          query: JSON.parse(windowKey),
        });
        if (mounted) {
          setData(payload);
          setLastUpdated(new Date());
        }
      } catch (e) {
        if (mounted) setError(e instanceof ApiError ? e.message : 'Failed to load economy health');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [request, env, windowKey, tick]);

  // Hourly auto-refresh.
  useEffect(() => {
    const id = setInterval(refetch, HOUR_MS);
    return () => clearInterval(id);
  }, [refetch]);

  return { data, loading, error, lastUpdated, refetch };
}
